<template>
  <div :class="collapseClass">
    <!-- collapse trigger -->
    <button
      :id="`cost-collapse-${id}`"
      class="w-100 rounded-0 p-0 text-dark-3 border-0 text-decoration-none"
      :aria-expanded="isExpanded"
      :aria-controls="id"
      :class="collapseClass"
      :data-testid="dataTestid"
      @click="toggle"
    >
      <div class="d-flex flex-row py-space-xs px-space-sm align-items-center">
        <!-- icon block -->
        <div class="me-space-xs d-flex align-items-center my-space-xxxs">
          <font-awesome-icon :icon="currentIcon"></font-awesome-icon>
        </div>
        <!-- title and total amount -->
        <div
          class="d-flex flex-row collapse-head-content align-items-center w-100 justify-content-between"
        >
          <h3 class="mb-0 text-capitalize fw-bold" :class="collapseTitleClass">
            {{ title }}
          </h3>
          <p v-if="!isExpanded" class="mb-0 fw-bold" :class="collapseTitleClass">
            {{ amount }}
          </p>
        </div>
      </div>
    </button>

    <!-- collapse breakdown -->
    <transition
      name="accordion"
      @enter="start"
      @after-enter="afterEnter"
      @before-leave="start"
      @after-leave="afterLeave"
    >
      <div
        v-show="isExpanded"
        :id="id"
        ref="collapse"
        class="collapse px-space-sm py-space-xs collapse-body fs-small border border-2"
        :class="collapseBodyClass"
        role="region"
        :aria-labelledby="`cost-collapse-${id}`"
        :data-testid="dataTestid ? `${dataTestid}-region` : undefined"
      >
        <div
          v-for="(item, index) in items"
          :key="index"
          class="d-flex flex-row collapse-body-content align-items-center border-bottom border-light-2"
        >
          <p class="my-space-xxs pe-space-md">{{ item.title }}</p>
          <p class="my-space-xxs">{{ item.amount }}</p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, useAttrs } from "vue";
import type { VariantOption } from "~/types/types";

interface CostItem {
  title: string;
  amount: string;
}

interface Props {
  id: string;
  title: string;
  amount: string;
  bgVariant?: VariantOption;
  borderVariant?: VariantOption;
  bodyVariant?: VariantOption;
  items: CostItem[];
  collapseTitleSize?: string;
}

defineOptions({ inheritAttrs: false });

const props = withDefaults(defineProps<Props>(), {
  bgVariant: "light-4",
  borderVariant: "light-2",
  bodyVariant: "white",
  collapseTitleSize: "small"
});

const emits = defineEmits<{
  /**
   *  emitted when the collapse is shown.
   */
  (e: "collapseShown" | "collapseHidden" | "open" | "close"): void;
  /**
   *  emitted when the collapse is hidden.
   */
  /**
   * emitted when the collapse is shown, for result compatibility.
   */
  /**
   * emitted when the collapse is hidden, for result compatibility.
   */
}>();

const attrs = useAttrs();
const dataTestid = computed(() => attrs["data-testid"] as string | undefined);
const isExpanded = ref(false);
const initialLoad = ref(false);

const currentIcon = computed(() => {
  return isExpanded.value ? "fa-solid fa-chevron-up" : "fa-solid fa-chevron-down";
});

const collapseClass = computed(() => {
  return `bg-${props.bgVariant} `;
});

const collapseBodyClass = computed(() => {
  return `bg-${props.bodyVariant} border-${props.borderVariant}`;
});

const collapseTitleClass = computed(() => {
  return `h3-${props.collapseTitleSize}`;
});

const toggle = (): void => {
  isExpanded.value = !isExpanded.value;
};

// collapse transition inspired by https://github.com/danielmalmros vue accordion
const start = (el: HTMLElement | Element) => {
  if (el instanceof HTMLElement) {
    if (isExpanded.value) {
      el.style.height = "0px";
      el.style.height = el.scrollHeight + "px";
    } else if (initialLoad.value) {
      el.style.height = el.scrollHeight + "px";
      setTimeout(() => {
        el.style.height = "0px";
      }, 10);
    }
    initialLoad.value = true;
  }
};

const end = (el: HTMLElement | Element) => {
  if (el instanceof HTMLElement) {
    el.style.height = "";
  }
};

const afterEnter = (el: HTMLElement | Element): void => {
  end(el);
  emits("collapseShown");
  emits("open");
};

const afterLeave = (el: HTMLElement | Element): void => {
  end(el);
  emits("collapseHidden");
  emits("close");
};
</script>

<style lang="scss" scoped>
.collapse-body {
  div:last-child {
    font-weight: bold;
  }
}
.collapse-body-content {
  p:first-child {
    flex-grow: 1;
  }
  &:last-of-type {
    border-bottom: 0px !important;
  }
}

.accordion-enter-active,
.accordion-leave-active {
  will-change: height, opacity;
  transition: height 0.2s ease;
  overflow: hidden;
}
</style>
